import MarkdownIt from "markdown-it";
import * as Sentry from "@sentry/vue";

import type { WelcomeVariant } from "./types/WelcomeFlowTypes";

export type WelcomeStepContent = {
  title: string;
  bodyHtml: string;
};

const EMPTY_STEP_CONTENT: WelcomeStepContent = { title: "", bodyHtml: "" };

// Same import.meta.glob approach as ModalMarkdown.vue, matched by exact variant/key path.
const welcomeMarkdownModules = import.meta.glob("@/content/welcome/**/index.md", {
  query: "?raw",
  import: "default",
  eager: false,
}) as Record<string, () => Promise<string>>;

const md = new MarkdownIt({ html: true });
// Route markdown-it's own output through the typography tokens — v-html content can't take
// Tailwind classes any other way. The leading "## " heading line is split off before rendering (see
// splitHeading below), so only the paragraph rule is needed here.
md.renderer.rules.paragraph_open = () => `<p class="t-body text-type-default">`;

// Every step file is "## Heading\n\nBody...". The heading renders as the card's own <h2> (so it can
// carry the aria-labelledby id), so it's split off rather than left to markdown-it's h-tag output.
const splitHeading = (raw: string): { title: string; body: string } => {
  const match = raw.trimStart().match(/^##[ \t]+([^\r\n]+)\r?\n([\s\S]*)$/);
  if (!match) return { title: "", body: raw };
  return { title: match[1].trim(), body: match[2] };
};

async function loadStep(variant: WelcomeVariant, key: string): Promise<WelcomeStepContent> {
  const moduleKey = Object.keys(welcomeMarkdownModules).find((path) => path.includes(`/${variant}/${key}/`));
  if (!moduleKey) {
    Sentry.captureException(new Error(`Welcome flow: no markdown found for ${variant}/${key}`));
    return EMPTY_STEP_CONTENT;
  }

  try {
    const { title, body } = splitHeading(await welcomeMarkdownModules[moduleKey]());
    return { title, bodyHtml: md.render(body) };
  } catch (error) {
    Sentry.captureException(error);
    return EMPTY_STEP_CONTENT;
  }
}

export function useWelcomeStepMarkdown() {
  return { loadStep };
}
